import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FaMoneyBillWave,
  FaClock,
  FaPause,
  FaLock,
  FaChartLine,
  FaEye,
  FaPhone,
  FaChevronRight,
} from 'react-icons/fa6';
import { TRUST_LINKS } from '../data/site';
import RgCallout from '../components/rgcallout';
import './innerpage.css';
import './responsiblegambling.css';

const TOOLS = [
  { Icon: FaMoneyBillWave, title: 'Deposit Limits', text: 'Set daily, weekly, or monthly deposit limits directly within your casino account. UKGC-licensed casinos are required to offer this feature.' },
  { Icon: FaClock, title: 'Session Time Limits', text: 'Receive notifications when you have been playing for a set amount of time. Helps prevent extended unplanned sessions.' },
  { Icon: FaPause, title: 'Take a Break', text: 'Temporarily pause your account for a set period — typically 24 hours to 6 weeks — without full self-exclusion.' },
  { Icon: FaLock, title: 'Self-Exclusion', text: 'Permanently or long-term exclude yourself from one or all UKGC-licensed casinos via GAMSTOP. This is a free service available to all UK players.' },
  { Icon: FaChartLine, title: 'Loss Limits', text: 'Cap the amount you can lose within a given time period. Some casinos also offer stake limits per spin or per bet.' },
  { Icon: FaEye, title: 'Reality Checks', text: 'Pop-up alerts during play that remind you how long you have been gambling and how much you have spent.' },
];

const HELPLINES = [
  { name: 'National Gambling Helpline', number: '0808 8020 133', hours: '24 hours a day, 7 days a week', url: 'https://www.gamcare.org.uk/' },
  { name: 'GamCare Live Chat', number: 'Available via GamCare website', hours: 'Select hours — check site', url: 'https://www.gamcare.org.uk/' },
  { name: 'Gamblers Anonymous UK', number: 'Check local meetings via website', hours: 'Meetings nationwide', url: 'https://www.gamblersanonymous.org.uk/' },
];

export default function ResponsibleGambling() {
  return (
    <>
      <Helmet>
        <title>Responsible Gambling — Tools, Support & Self-Exclusion | London Casino Deals</title>
        <meta name="description" content="Find responsible gambling tools, helplines, and self-exclusion options for UK players. GAMSTOP, GamCare, and BeGambleAware resources listed clearly." />
        <meta property="og:title" content="Responsible Gambling Guide for UK Players — London Casino Deals" />
        <meta property="og:description" content="UK gambling support tools, helplines, and self-exclusion services. Free resources for players who want to gamble more safely or seek help." />
        <link rel="canonical" href="https://londoncasinodeals.com/responsible-gambling" />
      </Helmet>

      <div className="inner-hero inner-hero--rg">
        <div className="container">
          <div className="inner-hero__breadcrumb">
            <a href="/">Home</a> <span aria-hidden="true">›</span> Responsible Gambling
          </div>
          <h1 className="inner-hero__title">Responsible Gambling</h1>
          <p className="inner-hero__subtitle">
            Gambling should be entertaining. If it stops being fun, free help is available around the clock.
          </p>
          <div className="rg-page__helpline-banner">
            <span className="rg-page__helpline-icon" aria-hidden="true">
              <FaPhone size={20} />
            </span>
            <strong>National Gambling Helpline: 0808 8020 133</strong>
            <span>Free · Confidential · Available 24/7</span>
          </div>
        </div>
      </div>

      <div className="inner-page-rg-slot">
        <div className="container">
          <RgCallout variant="compact" />
        </div>
      </div>

      <section className="section section--white">
        <div className="container">
          <div className="inner-intro">
            <h2>Gambling Is a Form of Entertainment — Not a Way to Make Money</h2>
            <p>
              All forms of gambling — casino games, slots, sports betting — carry a mathematical advantage for the operator. Over time, the house always wins. This is not a flaw; it is how gambling is designed. Understanding this is the foundation of responsible play.
            </p>
            <p>
              Signs that gambling may have become a problem include: gambling with money you cannot afford to lose, chasing losses, lying about gambling activity, or gambling interfering with work, relationships, or mental health. If any of these apply to you or someone you know, the resources on this page provide free, confidential support.
            </p>
          </div>
        </div>
      </section>

      <section className="section section--darker">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Tools Available at All UKGC Casinos</span>
            <h2 className="section__title">Responsible Gambling Tools</h2>
            <p className="section__subtitle">Every UKGC-licensed casino is required by law to offer these player protection tools</p>
          </div>
          <div className="rg-tools-grid">
            {TOOLS.map((tool) => (
              <div key={tool.title} className="rg-tool-card">
                <div className="rg-tool-card__icon" aria-hidden="true">
                  <tool.Icon size={26} />
                </div>
                <h3 className="rg-tool-card__title">{tool.title}</h3>
                <p className="rg-tool-card__text">{tool.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--white">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Organisations That Can Help</span>
            <h2 className="section__title">Safer Gambling Resources</h2>
            <p className="section__subtitle">All services below are free, confidential, and independent from casino operators</p>
          </div>
          <div className="rg-orgs-grid">
            {TRUST_LINKS.map((org) => (
              <a
                key={org.name}
                href={org.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="rg-org-card"
              >
                <span className="rg-org-card__logo-wrap">
                  <img src={org.logo} alt="" className="rg-org-card__logo" />
                </span>
                <div>
                  <div className="rg-org-card__name">{org.name}</div>
                  <div className="rg-org-card__desc">{org.description}</div>
                  <div className="rg-org-card__link">
                    Visit website <FaChevronRight size={11} className="rg-org-card__link-ic" aria-hidden />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--darker">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Free Help Lines</span>
            <h2 className="section__title">UK Gambling Support Helplines</h2>
          </div>
          <div className="helplines-list">
            {HELPLINES.map((h) => (
              <a key={h.name} href={h.url} target="_blank" rel="noopener noreferrer nofollow" className="helpline-card">
                <div className="helpline-card__name">{h.name}</div>
                <div className="helpline-card__number">{h.number}</div>
                <div className="helpline-card__hours">{h.hours}</div>
                <div className="helpline-card__cta">
                  Visit <FaChevronRight size={11} aria-hidden />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--white">
        <div className="container">
          <div className="gamstop-section">
            <div className="gamstop-section__content">
              <h2>Self-Exclude from All UK Online Casinos at Once</h2>
              <p>
                GAMSTOP is a free national self-exclusion service. Registering with GAMSTOP will prevent you from accessing all participating online gambling sites licensed by the UK Gambling Commission for a chosen period of 6 months, 1 year, or 5 years.
              </p>
              <p>
                It is free to register, takes only a few minutes, and comes into effect within 24 hours. You cannot reverse a GAMSTOP self-exclusion during the chosen period.
              </p>
              <a href="https://www.gamstop.co.uk/" target="_blank" rel="noopener noreferrer nofollow" className="btn btn--primary btn--lg">
                Register with GAMSTOP
              </a>
            </div>
          </div>
          <p className="inner-disclaimer" style={{ marginTop: 32 }}>
            London Casino Deals is a commercial affiliate website. We are not a gambling operator and cannot access your gambling accounts. For safeguarding measures, contact your casino operator or the organisations listed above directly. 18+ · Gambling involves risk.
          </p>
        </div>
      </section>
    </>
  );
}
